import { useMemo, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useAtom, atom } from 'jotai';
import { ReactTable, Tooltip } from '@crazyegginc/hatch';

import { useSearchParams, useModal, usePermissions, useNeedsUpgradeToAccessFeature, useSite } from '/src/hooks';
import { formatDate } from '/src/utils/date';
import { generateRecordingsFilterUrlForGoal } from '/src/features/goals/utils';
import { getFeatureName } from '/src/features/_global/utils';

// ICONS & ILLUSTRATIONS
import { ReactComponent as InfoIcon } from '@crazyegginc/hatch/dist/images/icon-info-circle-outline.svg';
import { ReactComponent as RecordingIcon } from '@crazyegginc/hatch/dist/images/icon-recording-filled.svg';
import { ReactComponent as ABTestIcon } from '@crazyegginc/hatch/dist/images/icon-ab-test-outline.svg';

// COMPONENTS
import { SEO } from '/src/components/SEO';
import { SitePickerBar } from '/src/components/site-picker';
import { DashboardPaywall } from '../../_global/paywalls/DashboardPaywall';
import { GoalsDashHeader } from '/src/features/goals/components/DashHeader';
import { GoalTriggerList } from '/src/features/goals/components/GoalTriggerList';
import { NoGoalsWall } from '../paywalls/NoGoalsWall';
import { AtomSearchInput } from '/src/components/search-input';
import { NoMatchesWall } from '../../_global/paywalls/NoMatchesWall';
import { DashboardPage } from '/src/components/Page';
import { AddSiteModal } from '/src/components/modals/AddSiteModal';
import { RecordingsUpgradeModal } from '/src/features/recordings/components/dashboard/modals/RecordingsUpgradeModal';
import { ABTestingUpgradeModal } from '/src/features/ab-testing/components/modals/ABTestingUpgradeModal';
import { InstallationBanner } from '/src/components/banners/InstallationBanner';
import { PaymentAlerts } from '/src/features/_global/components/PaymentAlerts';

import { goalListQuery } from '/src/features/goals/queries';

import { FEATURES, SORT_ORDER_TYPES } from '/src/features/_global/constants';

import { camelToSnake, snakeToCamel } from '/src/utils/string';
import {
  ManageSitesButton,
  SourceSelector,
  ViewAllSitesButton,
  PickerActionButton,
} from '/src/components/source-selector/source-selector';

const filterAtom = atom({ search: '' });

// HEADER CONTENT :START
// NOTE: PLACE FOR FILTER AND I THINK THE TITLE IS CONNECTED TO THE FILTER
function GoalsActions() {
  const [params] = useSearchParams();

  return (
    <div className="mb[15px] mb-[15px] flex w-full items-end justify-between">
      <h2 className="text-header-3">All goals</h2>

      <AtomSearchInput
        atom={filterAtom}
        initialValue={params.search}
        placeholder="Search for goal or URL"
        className="!bg-solitude-500 !placeholder-dodger-blue-500/50"
        ariaLabel="search for goal or URL"
      />
    </div>
  );
}

// GOAL TABLE CONTENT :START
// NOTE: MAKE SURE CREATED COLUMN IS SORTABLE AND UPDATE RECORDING AND A/B TEST ARIA LABEL IN THE ICONS

function GoalsTable({ goals, fetching, hasNextPage, loadNextPage }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const { sites, selectedSite } = useSite({ dontSelectSite: true });
  const needsUpgradeToAccessRecordings = useNeedsUpgradeToAccessFeature(FEATURES.RECORDINGS);
  const needsUpgradeToAccessABTesting = useNeedsUpgradeToAccessFeature(FEATURES.AB_TESTING);
  const modal = useModal();

  const getSiteName = useCallback(
    (cellRowSiteId) => {
      if (!selectedSite?.name) {
        return sites.find((x) => cellRowSiteId === x.id)?.name ?? null;
      }

      return selectedSite?.name;
    },
    [selectedSite?.name, sites],
  );

  const columns = useMemo(
    () => [
      {
        header: 'Goal',
        accessorKey: 'name',
        size: 340,
        meta: {
          align: 'center',
          justify: 'left',
          padding: '0px 8px',
        },
        cell: function FingerprintCell({ row }) {
          const haveTriggers = row?.original?.triggers ?? false;

          return (
            <div className="flex min-w-0 pr-5">
              <div className="flex w-full items-center">
                <div className="flex flex-1 items-center overflow-hidden">
                  {row.original.permissions.canView ? (
                    <Link to={`/goals/${row.original.id}`} className="text-link truncate">
                      {row.original.name}
                    </Link>
                  ) : (
                    <span className="text-link truncate">{row.original.name}</span>
                  )}
                </div>
                {haveTriggers && row?.original?.triggers.length > 0 && (
                  <Tooltip
                    interactive
                    placement="right"
                    darkTheme={true}
                    style={{ padding: '0px' }}
                    tooltipContent={
                      <GoalTriggerList triggers={row?.original?.triggers} defaultIcon={row.original.icon} />
                    }
                  >
                    <InfoIcon
                      className="h-4 w-4 -translate-y-px transform fill-current text-dodger-blue-300 ml-2.5"
                      aria-label="Trigger list"
                    />
                  </Tooltip>
                )}
              </div>
            </div>
          );
        },
      },
      {
        header: 'Created',
        accessorKey: 'createdAt',
        size: 90,
        meta: {
          align: 'center',
          justify: 'left',
        },
        cell: function GoalsCreatedAtCell({ row }) {
          return (
            <div className="flex w-full flex-col items-start">
              <span>{formatDate(row.original.createdAt)}</span>
              {row.original.createdBy && (
                <div className="text-caption w-full min-w-0 truncate">
                  <span className="truncate">by {row?.original?.createdBy?.name ?? row.original.createdBy.login}</span>
                </div>
              )}
            </div>
          );
        },
      },
      {
        header: function GoalsConversionsHeader() {
          return (
            <div className="flex items-center">
              <div className="mr-1.5 leading-none">Conversions</div>
              <Tooltip
                tooltipContent={
                  <div className="w-[335px] px-2 py-2.5">
                    <h5 className="mb-2.5 text-xs font-bold text-white">
                      Does this look different to your A/B test conversions?
                    </h5>
                    <p className="text-xs font-light leading-tight tracking-wide text-white">
                      Conversions for A/B tests are only counted if a visitor has landed on your test pages before
                      performing a goal, whereas all conversions are counted in your goals dashboard.
                    </p>
                  </div>
                }
              >
                <InfoIcon
                  className="h-3 w-3 -translate-y-px transform fill-current text-dodger-blue-300"
                  aria-label="Info on conversion"
                />
              </Tooltip>
            </div>
          );
        },
        accessorKey: 'conversions',
        size: 110,
        meta: {
          align: 'center',
          justify: 'right',
        },
        cell: function GoalsConversionsCell({ row }) {
          return row?.original?.conversionsCount.toLocaleString() || 0;
        },
      },
      {
        header: 'Recordings',
        size: 80,
        meta: {
          align: 'center',
          justify: 'right',
        },
        cell: ({ row }) => {
          return needsUpgradeToAccessRecordings ? (
            <button type="button" onClick={() => modal.show(<RecordingsUpgradeModal />)}>
              <RecordingIcon
                className="h-4 w-4 fill-current text-malibu-500"
                aria-label={`link to recordings related to ${row.original.name}`}
              />
            </button>
          ) : (
            <Link
              to={{
                pathname: '/recordings',
                search: `filters=${generateRecordingsFilterUrlForGoal(row.original)}&site=${getSiteName(
                  row.original.siteId,
                )}`,
              }}
              className="flex items-center"
            >
              <span className="mr-2">{row?.original?.sessionsCount.toLocaleString() || 0}</span>
              <RecordingIcon
                className="h-4 w-4 fill-current text-malibu-500"
                aria-label={`link to recordings related to ${row.original.name}`}
              />
            </Link>
          );
        },
      },
      {
        header: 'a/b test',
        accessorKey: 'abTests',
        size: 80,
        meta: {
          align: 'center',
          justify: 'right',
        },
        cell: ({ row }) => {
          return needsUpgradeToAccessABTesting ? (
            <button type="button" className="mr-2 " onClick={() => modal.show(<ABTestingUpgradeModal />)}>
              <ABTestIcon
                className="h-4 w-4 fill-current text-malibu-500"
                aria-label={`link to A/B Tests related to ${row.original.name}`}
              />
            </button>
          ) : (
            <>
              {row?.original?.abTestsCountActive.toLocaleString() || 0}
              <Link
                to={{
                  pathname: `/goals/${row.original.id}`,
                  hash: 'ab-tests',
                }}
                className="ml-2 flex items-center"
              >
                <ABTestIcon
                  className="h-4 w-4 fill-current text-malibu-500"
                  aria-label={`link to A/B Tests related to ${row.original.name}`}
                />
              </Link>
            </>
          );
        },
      },
    ],
    [getSiteName, modal, needsUpgradeToAccessRecordings, needsUpgradeToAccessABTesting],
  );

  const tableRef = useRef(null);

  const tableSortData = useMemo(() => {
    return {
      id: snakeToCamel(searchParams.get('order') ?? 'CREATED_AT'),
      desc: (searchParams.get('sort') ?? SORT_ORDER_TYPES.DESC) === SORT_ORDER_TYPES.DESC,
    };
  }, [searchParams]);

  const onFetchData = useCallback(
    ({ sorting }) => {
      if (sorting) {
        setSearchParams({
          order: camelToSnake(sorting[0].id).toUpperCase(),
          sort: sorting[0].desc ? SORT_ORDER_TYPES.DESC : SORT_ORDER_TYPES.ASC,
        });
      }
    },
    [setSearchParams],
  );

  return (
    <ReactTable
      columns={columns}
      data={goals}
      fetching={fetching}
      ref={{ tableRef }}
      enableSorting={true}
      sorting={tableSortData}
      onFetchData={onFetchData}
      rowHeight={90}
      dynamicRowHeight={true}
      hasNextPage={hasNextPage}
      isNextPageLoading={fetching}
      loadNextPage={loadNextPage}
      useVirtualization={true}
      rowPadding={true}
      rowCustomClassname={() => 'py-3.75 pr-10'}
      headerCustomClassname="!pr-10"
    />
  );
}

// CONTENT FOR NOGOALWALL, GOALSACTIONS, GOALSTABLE COMPONENTS :START
// NOTE: AT THE MOMENT USING SITES LENGTH TO SHOW THE CONTENT
function GoalsDashContent() {
  const [searchParams] = useSearchParams();
  const permissions = usePermissions();
  const { sites, loadingSites, selectedSite, reloadSites } = useSite({ enableAllSites: true });

  const canEditSites = permissions.can('create', FEATURES.SITES).allowed;

  const [filter, setFilter] = useAtom(filterAtom);

  const navigate = useNavigate();
  const {
    data: goalsData,
    error,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteQuery({
    ...goalListQuery({
      siteId: selectedSite?.id ?? undefined,
      search: filter.search && filter.search.length > 0 ? filter.search : undefined,
      order: searchParams.get('order'),
      sort: searchParams.get('sort'),
    }),
    enabled: Boolean(selectedSite),
  });

  const isLoading = !goalsData && !error;

  const goals = useMemo(
    () => (goalsData?.pages ?? []).reduce((acc, page) => [...acc, ...(page?.goalList?.list ?? [])], []) ?? [],
    [goalsData],
  );

  if (!loadingSites && !sites.length && canEditSites) {
    return (
      <AddSiteModal
        customTitle="Add a site before creating Goals"
        onExtraSteps={() => {
          reloadSites();
        }}
        onCancel={() => navigate('/snapshots', { replace: true })}
      />
    );
  }

  if (!isLoading && goals.length === 0 && !filter.search) {
    return <NoGoalsWall />;
  }

  return (
    <>
      <InstallationBanner />
      <div className="mt-10 flex w-full flex-col px-10">
        <GoalsActions />
        <div className="flex w-full justify-center">
          {!isLoading && goals.length === 0 && filter.search.length > 0 ? (
            <NoMatchesWall text="matches for this search" reset={() => setFilter({ search: '' })} actionText="search" />
          ) : (
            <GoalsTable goals={goals} fetching={isLoading} hasNextPage={hasNextPage} loadNextPage={fetchNextPage} />
          )}
        </div>
      </div>
    </>
  );
}

// ALL COMPONENTS :START
// eslint-disable-next-line no-restricted-syntax
export default function GoalsDashboard() {
  const { sites, loadingSites, selectedSite, selectSite } = useSite({ enableAllSites: true });
  const permissions = usePermissions();
  const { allowed: canViewDashboard, reason } = permissions.can('view', FEATURES.GOALS);
  const canEditSites = permissions.can('create', FEATURES.SITES).allowed;

  if (!canViewDashboard) {
    return <DashboardPaywall feature={FEATURES.GOALS} reason={reason} />;
  }

  return (
    <DashboardPage>
      <SEO title={getFeatureName(FEATURES.GOALS)} />
      <GoalsDashHeader />
      <SitePickerBar
        Picker={SourceSelector}
        pickerClass="absolute top-full left-0"
        actionButtons={[
          <PickerActionButton key="picker_btn_ga" onClick={() => {}}>
            Google Analytics Settings
          </PickerActionButton>,
          <PickerActionButton key="picker_btn_sites" onClick={() => {}}>
            Enable Other Sites
          </PickerActionButton>,
          <ViewAllSitesButton key="picker_btn_all_sites" />,
          <ManageSitesButton key="picker_btn_manage_sites" />,
        ]}
        sites={sites}
        loading={loadingSites}
        selectedSite={selectedSite}
        selectSite={selectSite}
        enableAllSites={true}
        addSiteOption={canEditSites}
        pageDisplayName={`${getFeatureName(FEATURES.GOALS)} dashboard`}
      />
      <PaymentAlerts />
      <GoalsDashContent />
    </DashboardPage>
  );
}
